import { useState, useEffect, useRef } from 'react';
import { adPlatformAPI, adPlacementAPI, adAnalyticsAPI, uploadAPI, BASE_URL } from '../services/api';
import './Brands.css';
import Icon from '../components/Icon';

function AdManagement() {
  const [activeTab, setActiveTab] = useState('platforms');

  // Platform state
  const [platforms, setPlatforms] = useState([]);
  const [platformTypes, setPlatformTypes] = useState([]);
  const [platformStatuses, setPlatformStatuses] = useState([]);

  // Placement state
  const [placements, setPlacements] = useState([]);
  const [adTypes, setAdTypes] = useState([]);
  const [placementStatuses, setPlacementStatuses] = useState([]);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [platformAnalytics, setPlatformAnalytics] = useState([]);
  const [placementAnalytics, setPlacementAnalytics] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Common state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formType, setFormType] = useState('platform');
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  // Filters
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [adTypeFilter, setAdTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // File upload state
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // Tab hover state
  const [hoveredTab, setHoveredTab] = useState(null);

  // Show alert helper
  const showAlert = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlertModal(true);
  };

  // Handle file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      showAlert('Invalid file type. Only JPEG, PNG, GIF, WebP, and SVG are allowed.', 'error');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showAlert('File is too large. Maximum size is 5MB.', 'error');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadPlatformImage(file);
      const imageUrl = response.data.url;
      setFormData({ ...formData, image: imageUrl });
      setImagePreview(BASE_URL +imageUrl);
      showAlert('Image uploaded successfully');
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = async () => {
    if (formData.image && formData.image.startsWith('/uploads/')) {
      const filename = formData.image.split('/').pop();
      try {
        await uploadAPI.deletePlatformImage(filename);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
    setFormData({ ...formData, image: '' });
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fetch platform dropdown data
  const fetchPlatformDropdowns = async () => {
    try {
      const [typesRes, statusesRes] = await Promise.all([
        adPlatformAPI.getTypes(),
        adPlatformAPI.getStatuses()
      ]);
      setPlatformTypes(typesRes.data || []);
      setPlatformStatuses(statusesRes.data || []);
    } catch (err) {
      console.error('Error fetching platform dropdowns:', err);
    }
  };

  // Fetch placement dropdown data
  const fetchPlacementDropdowns = async () => {
    try {
      const [typesRes, statusesRes] = await Promise.all([
        adPlacementAPI.getTypes(),
        adPlacementAPI.getStatuses()
      ]);
      setAdTypes(typesRes.data || []);
      setPlacementStatuses(statusesRes.data || []);
    } catch (err) {
      console.error('Error fetching placement dropdowns:', err);
    }
  };

  // Fetch platforms
  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await adPlatformAPI.getAll(params);
      setPlatforms(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching platforms:', err);
      setError('Failed to fetch ad platforms');
    } finally {
      setLoading(false);
    }
  };

  // Fetch placements
  const fetchPlacements = async () => {
    try {
      setLoading(true);
      const params = {};
      if (platformFilter) params.platformId = platformFilter;
      if (statusFilter) params.status = statusFilter;
      if (adTypeFilter) params.adType = adTypeFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await adPlacementAPI.getAll(params);
      setPlacements(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching placements:', err);
      setError('Failed to fetch ad placements');
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      const [overviewRes, platformRes, placementRes, eventsRes] = await Promise.all([
        adAnalyticsAPI.getOverview(params),
        adAnalyticsAPI.getByPlatform(params),
        adAnalyticsAPI.getByPlacement(params),
        adAnalyticsAPI.getRecentEvents({ limit: 20 })
      ]);
      setAnalytics(overviewRes.data || null);
      setPlatformAnalytics(platformRes.data || []);
      setPlacementAnalytics(placementRes.data || []);
      setRecentEvents(eventsRes.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchPlatformDropdowns();
    fetchPlacementDropdowns();
  }, []);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'platforms') {
      fetchPlatforms();
    } else if (activeTab === 'placements') {
      fetchPlacements();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter, platformFilter, adTypeFilter, searchQuery, dateRange]);

  // Handle platform form submit
  const handlePlatformSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingItem) {
        await adPlatformAPI.update(editingItem.id, formData);
        showAlert('Platform updated successfully');
      } else {
        await adPlatformAPI.create(formData);
        showAlert('Platform created successfully');
      }
      setShowFormModal(false);
      fetchPlatforms();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save platform', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle placement form submit
  const handlePlacementSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingItem) {
        await adPlacementAPI.update(editingItem.id, formData);
        showAlert('Placement updated successfully');
      } else {
        await adPlacementAPI.create(formData);
        showAlert('Placement created successfully');
      }
      setShowFormModal(false);
      fetchPlacements();
      fetchPlacementDropdowns();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save placement', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      if (type === 'platform') {
        await adPlatformAPI.delete(id);
        fetchPlatforms();
      } else {
        await adPlacementAPI.delete(id);
        fetchPlacements();
      }
      showAlert(`${type === 'platform' ? 'Platform' : 'Placement'} deleted successfully`);
    } catch (err) {
      showAlert(err.response?.data?.error || `Failed to delete ${type}`, 'error');
    }
  };

  // Open create modal
  const openCreateModal = (type) => {
    setFormType(type);
    setEditingItem(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (type === 'platform') {
      setFormData({
        platformId: '',
        name: '',
        type: 'ADMOB',
        status: 'ACTIVE',
        image: '',
        appId: '',
        apiKey: '',
        notes: ''
      });
    } else {
      setFormData({
        placementId: '',
        name: '',
        platformId: platforms.length > 0 ? platforms[0].id : '',
        adUnitId: '',
        adType: 'BANNER',
        screenName: '',
        position: '',
        status: 'ACTIVE',
        refreshRate: 0,
        notes: ''
      });
    }
    setShowFormModal(true);
  };

  // Open edit modal
  const openEditModal = (type, item) => {
    setFormType(type);
    setEditingItem(item);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (type === 'platform') {
      // Set image preview for existing images
      if (item.image) {
        if (item.image.startsWith('/uploads/')) {
          setImagePreview(BASE_URL +item.image);
        } else {
          setImagePreview(item.image);
        }
      } else {
        setImagePreview('');
      }
      setFormData({
        platformId: item.platformId,
        name: item.name,
        type: item.type,
        status: item.status,
        image: item.image || '',
        appId: item.appId || '',
        apiKey: item.apiKey || '',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        placementId: item.placementId,
        name: item.name,
        platformId: item.platformId,
        adUnitId: item.adUnitId,
        adType: item.adType,
        screenName: item.screenName,
        position: item.position || '',
        status: item.status,
        refreshRate: item.refreshRate || 0,
        notes: item.notes || ''
      });
    }
    setShowFormModal(true);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return '#10b981';
      case 'INACTIVE': return '#ef4444';
      case 'TESTING': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  // Get ad type badge color
  const getAdTypeColor = (type) => {
    switch (type) {
      case 'BANNER': return '#3b82f6';
      case 'INTERSTITIAL': return '#4338ca';
      case 'REWARDED': return '#10b981';
      case 'NATIVE': return '#f59e0b';
      case 'APP_OPEN': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Render platforms tab
  const renderPlatformsTab = () => (
    <div className="card">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Ad Platforms</h1>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>{platforms.length} platforms</span>
        </div>
        <button className="btn btn-primary" onClick={() => openCreateModal('platform')}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Platform
        </button>
      </div>

      {/* Filters */}
      <div className="table-controls">
        <div className="table-filters">
          <input
            type="text"
            placeholder="Search platforms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            {platformStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Platform list */}
      {loading ? (
        <div className="loading">Loading platforms...</div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : platforms.length === 0 ? (
        <div className="empty-state">
          <p>No ad platforms found.</p>
          <button className="btn btn-primary" onClick={() => openCreateModal('platform')} style={{ marginTop: '16px' }}>
            Add Your First Platform
          </button>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Platform</th>
              <th>Type</th>
              <th>App ID</th>
              <th>Status</th>
              <th>Placements</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {platforms.map(platform => (
              <tr key={platform.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {platform.image ? (
                      <img
                        src={platform.image.startsWith('/uploads/') ? BASE_URL +platform.image : platform.image}
                        alt={platform.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          objectFit: 'contain',
                          backgroundColor: '#f3f4f6'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
                      display: platform.image ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      {platform.type.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500' }}>{platform.name}</div>
                      <code>{platform.platformId}</code>
                    </div>
                  </div>
                </td>
                <td>{platform.type}</td>
                <td>
                  {platform.appId ? (
                    <code style={{ fontSize: '11px' }}>{platform.appId.substring(0, 25)}...</code>
                  ) : '-'}
                </td>
                <td>
                  <span className="badge" style={{
                    backgroundColor: `${getStatusColor(platform.status)}20`,
                    color: getStatusColor(platform.status)
                  }}>
                    {platform.status}
                  </span>
                </td>
                <td>{platform._count?.placements || 0}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-edit btn-sm" onClick={() => openEditModal('platform', platform)}>
                      Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete('platform', platform.id, platform.name)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // Render placements tab
  const renderPlacementsTab = () => (
    <div className="card">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Ad Placements</h1>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>{placements.length} placements</span>
        </div>
        <button className="btn btn-primary" onClick={() => openCreateModal('placement')} disabled={platforms.length === 0}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Placement
        </button>
      </div>

      {/* Filters */}
      <div className="table-controls">
        <div className="table-filters">
          <input
            type="text"
            placeholder="Search placements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Platforms</option>
            {platforms.map(platform => (
              <option key={platform.id} value={platform.id}>{platform.name}</option>
            ))}
          </select>
          <select
            value={adTypeFilter}
            onChange={(e) => setAdTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Ad Types</option>
            {adTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            {placementStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Placement list */}
      {loading ? (
        <div className="loading">Loading placements...</div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : platforms.length === 0 ? (
        <div className="empty-state">
          <p>Please create an ad platform first before adding placements.</p>
          <button className="btn btn-primary" onClick={() => setActiveTab('platforms')} style={{ marginTop: '16px' }}>
            Go to Platforms
          </button>
        </div>
      ) : placements.length === 0 ? (
        <div className="empty-state">
          <p>No ad placements found.</p>
          <button className="btn btn-primary" onClick={() => openCreateModal('placement')} style={{ marginTop: '16px' }}>
            Add Your First Placement
          </button>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Placement</th>
              <th>Platform</th>
              <th>Ad Type</th>
              <th>Screen</th>
              <th>Status</th>
              <th>Events</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {placements.map(placement => (
              <tr key={placement.id}>
                <td>
                  <div>
                    <div style={{ fontWeight: '500' }}>{placement.name}</div>
                    <code>{placement.placementId}</code>
                  </div>
                </td>
                <td>
                  <div style={{ fontSize: '14px' }}>{placement.platform?.name}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{placement.platform?.type}</div>
                </td>
                <td>
                  <span className="badge" style={{
                    backgroundColor: `${getAdTypeColor(placement.adType)}15`,
                    color: getAdTypeColor(placement.adType)
                  }}>
                    {placement.adType}
                  </span>
                </td>
                <td>{placement.screenName}</td>
                <td>
                  <span className="badge" style={{
                    backgroundColor: `${getStatusColor(placement.status)}20`,
                    color: getStatusColor(placement.status)
                  }}>
                    {placement.status}
                  </span>
                </td>
                <td>{placement._count?.events || 0}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-edit btn-sm" onClick={() => openEditModal('placement', placement)}>
                      Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete('placement', placement.id, placement.name)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // Render analytics tab
  const renderAnalyticsTab = () => (
    <div>
      {/* Date Range Card */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1>Ad Analytics</h1>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="search-input"
              style={{ width: 'auto' }}
            />
            <span style={{ color: '#6b7280' }}>to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="search-input"
              style={{ width: 'auto' }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="loading">Loading analytics...</div></div>
      ) : error ? (
        <div className="card"><div className="alert alert-error">{error}</div></div>
      ) : (
        <>
          {/* Overview Cards */}
          {analytics && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Requests</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a2e' }}>{analytics.totalRequests.toLocaleString()}</div>
              </div>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Ads Loaded</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{analytics.adsLoaded.toLocaleString()}</div>
              </div>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Impressions</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{analytics.impressions.toLocaleString()}</div>
              </div>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Fill Rate</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#4338ca' }}>{analytics.fillRate}%</div>
              </div>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Show Rate</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>{analytics.showRate}%</div>
              </div>
              <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Failed</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>{analytics.adsFailed.toLocaleString()}</div>
              </div>
            </div>
          )}

          {/* Platform Analytics */}
          {platformAnalytics.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>Analytics by Platform</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Type</th>
                    <th>Requests</th>
                    <th>Loaded</th>
                    <th>Shown</th>
                    <th>Impressions</th>
                    <th>Fill Rate</th>
                    <th>Show Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {platformAnalytics.map(pa => (
                    <tr key={pa.platformId}>
                      <td style={{ fontWeight: '500' }}>{pa.platformName}</td>
                      <td>{pa.platformType}</td>
                      <td>{pa.totalRequests.toLocaleString()}</td>
                      <td>{pa.adsLoaded.toLocaleString()}</td>
                      <td>{pa.adsShown.toLocaleString()}</td>
                      <td>{pa.impressions.toLocaleString()}</td>
                      <td style={{ color: pa.fillRate >= 80 ? '#10b981' : pa.fillRate >= 50 ? '#f59e0b' : '#ef4444', fontWeight: '500' }}>
                        {pa.fillRate}%
                      </td>
                      <td style={{ color: pa.showRate >= 80 ? '#10b981' : pa.showRate >= 50 ? '#f59e0b' : '#ef4444', fontWeight: '500' }}>
                        {pa.showRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Placement Analytics */}
          {placementAnalytics.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>Analytics by Placement</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Placement</th>
                    <th>Screen</th>
                    <th>Ad Type</th>
                    <th>Requests</th>
                    <th>Loaded</th>
                    <th>Impressions</th>
                    <th>Fill Rate</th>
                    <th>Show Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {placementAnalytics.map(pa => (
                    <tr key={pa.placementId}>
                      <td>
                        <div style={{ fontWeight: '500' }}>{pa.placementName}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{pa.platformName}</div>
                      </td>
                      <td>{pa.screenName}</td>
                      <td>
                        <span className="badge" style={{
                          backgroundColor: `${getAdTypeColor(pa.adType)}15`,
                          color: getAdTypeColor(pa.adType)
                        }}>
                          {pa.adType}
                        </span>
                      </td>
                      <td>{pa.totalRequests.toLocaleString()}</td>
                      <td>{pa.adsLoaded.toLocaleString()}</td>
                      <td>{pa.impressions.toLocaleString()}</td>
                      <td style={{ color: pa.fillRate >= 80 ? '#10b981' : pa.fillRate >= 50 ? '#f59e0b' : '#ef4444', fontWeight: '500' }}>
                        {pa.fillRate}%
                      </td>
                      <td style={{ color: pa.showRate >= 80 ? '#10b981' : pa.showRate >= 50 ? '#f59e0b' : '#ef4444', fontWeight: '500' }}>
                        {pa.showRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent Events */}
          {recentEvents.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>Recent Events</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Placement</th>
                    <th>Event</th>
                    <th>Platform</th>
                    <th>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEvents.map(event => (
                    <tr key={event.id}>
                      <td style={{ fontSize: '12px' }}>{new Date(event.eventTime).toLocaleString()}</td>
                      <td>{event.placement?.name}</td>
                      <td>
                        <span className="badge" style={{
                          backgroundColor: event.eventType.includes('FAILED') || event.eventType.includes('NOT') ? '#fee2e2' : '#dcfce7',
                          color: event.eventType.includes('FAILED') || event.eventType.includes('NOT') ? '#ef4444' : '#10b981'
                        }}>
                          {event.eventType}
                        </span>
                      </td>
                      <td>{event.platform || '-'}</td>
                      <td style={{ fontSize: '12px', color: '#6b7280' }}>{event.deviceModel || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render form modal
  const renderFormModal = () => (
    showFormModal && (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2>{editingItem ? `Edit ${formType === 'platform' ? 'Platform' : 'Placement'}` : `Add ${formType === 'platform' ? 'Platform' : 'Placement'}`}</h2>
            <button className="modal-close" onClick={() => setShowFormModal(false)}>×</button>
          </div>
          <form onSubmit={formType === 'platform' ? handlePlatformSubmit : handlePlacementSubmit}>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              {formType === 'platform' ? (
                <>
                  <div className="form-group">
                    <label>Platform ID *</label>
                    <input
                      type="text"
                      value={formData.platformId}
                      onChange={(e) => setFormData({ ...formData, platformId: e.target.value })}
                      placeholder="e.g., admob_001"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Google AdMob"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      >
                        {platformTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        {platformStatuses.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Platform Image</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Image preview */}
                      {(imagePreview || formData.image) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <img
                            src={imagePreview || (formData.image?.startsWith('/uploads/') ? BASE_URL +formData.image : formData.image)}
                            alt="Preview"
                            style={{
                              width: '64px',
                              height: '64px',
                              borderRadius: '8px',
                              objectFit: 'contain',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #e5e7eb'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #ef4444',
                              borderRadius: '6px',
                              background: '#fef2f2',
                              color: '#ef4444',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      {/* File upload input */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="platform-image-upload"
                        />
                        <label
                          htmlFor="platform-image-upload"
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: '#fff',
                            color: '#374151',
                            fontSize: '14px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: uploading ? 0.6 : 1
                          }}
                        >
                          {uploading ? (
                            <>
                              <span style={{ animation: 'spin 1s linear infinite' }}>&#x21bb;</span>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {formData.image ? 'Change Image' : 'Upload Image'}
                            </>
                          )}
                        </label>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>PNG, JPG, GIF, WebP, SVG (max 5MB)</span>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>App ID</label>
                    <input
                      type="text"
                      value={formData.appId}
                      onChange={(e) => setFormData({ ...formData, appId: e.target.value })}
                      placeholder="SDK App ID"
                    />
                  </div>
                  <div className="form-group">
                    <label>API Key</label>
                    <input
                      type="text"
                      value={formData.apiKey}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                      placeholder="API Key or Secret"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows="3"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Placement ID *</label>
                    <input
                      type="text"
                      value={formData.placementId}
                      onChange={(e) => setFormData({ ...formData, placementId: e.target.value })}
                      placeholder="e.g., home_banner_01"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Home Banner"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Platform *</label>
                    <select
                      value={formData.platformId}
                      onChange={(e) => setFormData({ ...formData, platformId: e.target.value })}
                      required
                    >
                      {platforms.map(platform => (
                        <option key={platform.id} value={platform.id}>{platform.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ad Unit ID *</label>
                    <input
                      type="text"
                      value={formData.adUnitId}
                      onChange={(e) => setFormData({ ...formData, adUnitId: e.target.value })}
                      placeholder="Ad unit ID from platform"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ad Type</label>
                      <select
                        value={formData.adType}
                        onChange={(e) => setFormData({ ...formData, adType: e.target.value })}
                      >
                        {adTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        {placementStatuses.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Screen Name *</label>
                      <input
                        type="text"
                        value={formData.screenName}
                        onChange={(e) => setFormData({ ...formData, screenName: e.target.value })}
                        placeholder="e.g., home, details"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Position</label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        placeholder="e.g., top, bottom"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Refresh Rate (seconds)</label>
                    <input
                      type="number"
                      value={formData.refreshRate}
                      onChange={(e) => setFormData({ ...formData, refreshRate: parseInt(e.target.value) || 0 })}
                      placeholder="0 for no refresh"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows="2"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ gap: '12px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowFormModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={formLoading}>
                {formLoading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  // Render alert modal
  const renderAlertModal = () => (
    showAlertModal && (
      <div className="modal-overlay" style={{ zIndex: 10000 }}>
        <div className="modal" style={{ maxWidth: '400px', zIndex: 10001 }}>
          <div className="modal-body">
            <div className={alertType === 'success' ? 'success-icon' : 'error-icon'}>
              <Icon name={alertType === 'success' ? 'check' : 'close'} size={14} />
            </div>
            <p style={{ fontSize: '16px', color: '#374151', marginBottom: '24px' }}>{alertMessage}</p>
            <button className="btn btn-primary" onClick={() => setShowAlertModal(false)}>
              OK
            </button>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className="brands-page">
      {/* Tabs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '20px',
        padding: '6px',
        backgroundColor: '#f1f5f9',
        borderRadius: '12px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => { setActiveTab('platforms'); setSearchQuery(''); setStatusFilter(''); }}
          style={{
            padding: '10px 18px',
            fontSize: '13px',
            fontWeight: activeTab === 'platforms' ? '600' : '500',
            color: activeTab === 'platforms' ? '#ffffff' : '#64748b',
            backgroundColor: activeTab === 'platforms' ? '#4f46e5' : (hoveredTab === 'platforms' ? '#e2e8f0' : 'transparent'),
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: activeTab === 'platforms' ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none'
          }}
          onMouseEnter={() => setHoveredTab('platforms')}
          onMouseLeave={() => setHoveredTab(null)}
        >
          <Icon name="broadcast" size={15} style={{ pointerEvents: 'none' }} />
          <span style={{ pointerEvents: 'none' }}>Platforms</span>
        </button>
        <button
          onClick={() => { setActiveTab('placements'); setSearchQuery(''); setStatusFilter(''); setPlatformFilter(''); setAdTypeFilter(''); }}
          style={{
            padding: '10px 18px',
            fontSize: '13px',
            fontWeight: activeTab === 'placements' ? '600' : '500',
            color: activeTab === 'placements' ? '#ffffff' : '#64748b',
            backgroundColor: activeTab === 'placements' ? '#4f46e5' : (hoveredTab === 'placements' ? '#e2e8f0' : 'transparent'),
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: activeTab === 'placements' ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none'
          }}
          onMouseEnter={() => setHoveredTab('placements')}
          onMouseLeave={() => setHoveredTab(null)}
        >
          <Icon name="pin" size={15} style={{ pointerEvents: 'none' }} />
          <span style={{ pointerEvents: 'none' }}>Placements</span>
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '10px 18px',
            fontSize: '13px',
            fontWeight: activeTab === 'analytics' ? '600' : '500',
            color: activeTab === 'analytics' ? '#ffffff' : '#64748b',
            backgroundColor: activeTab === 'analytics' ? '#4f46e5' : (hoveredTab === 'analytics' ? '#e2e8f0' : 'transparent'),
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: activeTab === 'analytics' ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none'
          }}
          onMouseEnter={() => setHoveredTab('analytics')}
          onMouseLeave={() => setHoveredTab(null)}
        >
          <Icon name="chart" size={15} style={{ pointerEvents: 'none' }} />
          <span style={{ pointerEvents: 'none' }}>Analytics</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'platforms' && renderPlatformsTab()}
      {activeTab === 'placements' && renderPlacementsTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}

      {/* Modals */}
      {renderFormModal()}
      {renderAlertModal()}
    </div>
  );
}

export default AdManagement;
